import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUpdateQuoteStatus } from '@/api/hooks';
import type { QuoteStatus } from '@/api/types';
import { STATUS_TRANSITIONS, statusBadgeProps } from '@/lib/statusTransitions';

interface StatusTransitionSheetProps {
  quoteId: string;
  currentStatus: QuoteStatus;
  isOpen: boolean;
  onClose: () => void;
  onStatusChanged: (newStatus: QuoteStatus) => void;
}

const DESTRUCTIVE_SOURCES = new Set<QuoteStatus>(['invoiced', 'paid']);

export function StatusTransitionSheet({
  quoteId,
  currentStatus,
  isOpen,
  onClose,
  onStatusChanged,
}: Readonly<StatusTransitionSheetProps>) {
  const { t } = useTranslation();
  const { getAccessToken } = useKindeAuth();
  const queryClient = useQueryClient();
  const updateStatus = useUpdateQuoteStatus(getAccessToken as () => Promise<string>);
  const [confirmingRevert, setConfirmingRevert] = useState(false);

  const validTransitions = STATUS_TRANSITIONS[currentStatus];

  const handleMutationSuccess = useCallback(
    (newStatus: QuoteStatus) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(t('quoteStatus.changed', { status: t(`quoteStatus.${newStatus}`) }));
      onStatusChanged(newStatus);
      onClose();
    },
    [queryClient, onStatusChanged, onClose, t],
  );

  const handleTransition = useCallback(
    (newStatus: QuoteStatus) => {
      if (newStatus === 'draft' && DESTRUCTIVE_SOURCES.has(currentStatus)) {
        setConfirmingRevert(true);
        return;
      }

      updateStatus.mutate(
        { id: quoteId, status: newStatus },
        {
          onSuccess: () => handleMutationSuccess(newStatus),
          onError: () => {
            toast.error(t('quoteStatus.updateFailed'));
          },
        },
      );
    },
    [quoteId, currentStatus, updateStatus, handleMutationSuccess, t],
  );

  const handleConfirmRevert = useCallback(() => {
    updateStatus.mutate(
      { id: quoteId, status: 'draft' },
      {
        onSuccess: () => {
          setConfirmingRevert(false);
          handleMutationSuccess('draft');
        },
        onError: () => {
          toast.error(t('quoteStatus.updateFailed'));
        },
      },
    );
  }, [quoteId, updateStatus, handleMutationSuccess, t]);

  const handleClose = useCallback(() => {
    setConfirmingRevert(false);
    onClose();
  }, [onClose]);

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-safe">
        <SheetHeader className="pb-4">
          <SheetTitle>
            {t('quoteStatus.current', {
              status: t(`quoteStatus.${currentStatus}`),
            })}
          </SheetTitle>
          <SheetDescription className="sr-only">{t('quoteStatus.label')}</SheetDescription>
        </SheetHeader>

        {confirmingRevert ? (
          <div className="space-y-3 px-1 pb-4">
            <p className="text-sm text-muted-foreground">{t('quoteStatus.revertWarning')}</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 min-h-[44px]"
                onClick={() => setConfirmingRevert(false)}
              >
                {t('quoteStatus.revertCancel')}
              </Button>
              <Button
                variant="destructive"
                className="flex-1 min-h-[44px]"
                disabled={updateStatus.isPending}
                onClick={handleConfirmRevert}
              >
                {t('quoteStatus.revertConfirm')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 px-1 pb-4">
            {validTransitions.map((status) => {
              const isDestructive = status === 'draft' && DESTRUCTIVE_SOURCES.has(currentStatus);
              const badge = statusBadgeProps(status);

              return (
                <Button
                  key={status}
                  variant={isDestructive ? 'destructive' : 'outline'}
                  className="w-full min-h-[48px] justify-start text-base"
                  disabled={updateStatus.isPending}
                  onClick={() => handleTransition(status)}
                >
                  {updateStatus.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Badge {...badge} className={`mr-2 ${badge.className ?? ''}`}>
                      {t(`quoteStatus.${status}`)}
                    </Badge>
                  )}
                  {t('quoteStatus.markAs', {
                    status: t(`quoteStatus.${status}`),
                  })}
                </Button>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
